import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import {
  sortByNumberProperty,
  sortByStringProperty,
} from "@shared/utils/general";
import {
  BotFormType,
  BulkEditBotData,
  ExchangeSymbolAmountData,
  FormDataType,
  UseSharedSymbolAndAccountSelectorsProps,
} from "@shared/features/bots/BotForm/common/types";
import {
  difference,
  intersection,
  mapValues,
  max,
  sum,
  union,
  uniq,
} from "lodash";
import {
  BotType,
  ClientData,
  LanguageNameSpaces,
  SortingOrder,
  SubGroup,
} from "@shared/types";
import { MARGIN_EXCHANGES } from "@shared/common/constants";
import { isSymbol } from "@shared/types/utils";
import { MAX_FETCH_ALL_ACCOUNT_AMOUNTS } from "@shared/features/bots/BotForm/common/constants";
import {
  useClientsData,
  useExchangesMetadata,
  useSymbolsMetadata,
} from "@shared/utils/hooks/misc";
import { BotFormContext } from "@shared/features/bots/BotForm/common/BotForm.context";
import { getMinUsdAmountPerTrade } from "@shared/features/bots/BotForm/common/utils";
import { sharedLib } from "@shared/services/shared.service";
import { skipToken } from "@reduxjs/toolkit/query";
import useFormHelperData from "@shared/features/bots/BotForm/common/hooks/useFormHelperData";
import useCopyBotToWorkspace from "@shared/features/bots/BotForm/useCopyBotToWorkspace";
import { useBotFormAccountAmounts } from "@shared/features/bots/BotForm/common/hooks/useBotFormAccountAmounts";
import {
  getPossibleEquivalentSymbols,
  sortItemsByExchangeOrder,
} from "@shared/utils/misc";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import { TV_ALERT_SYMBOL } from "@shared/features/bots/constants";

const useSharedSymbolAndAccountSelectors = ({
  defaultAccountId,
  defaultExchange,
  defaultSymbols: propsDefaultSymbols,
  defaultBlacklistSymbols,
  initialize,
  openMessage,
  symbols: propsSymbols,
  setSymbols: propsSetSymbols,
  account: propsAccount,
  setAccount: propsSetAccount,
  isTrade,
  onSymbolChanged,
  onAccountChanged,
  useFreeText,
  onChangeWithWarning,
}: UseSharedSymbolAndAccountSelectorsProps) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.COMMON);
  const {
    symbols: botFormSymbols,
    setSymbols: botFormSetSymbols,
    account: botFormAccount,
    setAccount: botFormSetAccount,
    accounts,
    setAccounts,
    botAmountData,
    publicBotGeneralData,
    botFormType,
    addInitializedData,
    bulkActionBotsData,
    botType,
    botsData,
    isChannelConfiguration,
    isMarginExchange,
    accountsOverlapData,
    isEdit,
    subGroup,
    isBacktesting,
    isCopyToWorkspace,
    initializedData,
    symbolsMetadata: botFormSymbolsMetadata,
    botUUID,
    isPublicBotApplied,
    shouldInitialSymbolAndAccount,
    setShouldInitialSymbolAndAccount,
    useSymbolFromAlert,
    setUseSymbolFromAlert,
  } = useContext(BotFormContext) || {};
  const { getHelperData } = useFormHelperData();
  const symbols = botFormSymbols ?? propsSymbols;
  const setSymbols = botFormSetSymbols ?? propsSetSymbols;
  const account = botFormAccount ?? propsAccount;
  const setAccount = botFormSetAccount ?? propsSetAccount;

  const isCopyPublic = botFormType === BotFormType.COPY_PUBLIC;
  const isBulkCreate = botFormType === BotFormType.BULK_CREATE;
  const isSignalsBot = botType === BotType.SIGNAL;
  const isHedgingBot = botType === BotType.HEDGING;

  const [isModifiedSymbols, setIsModifiedSymbols] = useState<boolean>();
  const [switchedExchange, setSwitchedExchange] = useState<string>();
  const { writableActiveClientsData, getAccountById, getClientsListStatus } =
    useClientsData();
  const shouldFetchAllAccountsAmounts =
    isBulkCreate ||
    (!!writableActiveClientsData &&
      writableActiveClientsData?.length <= MAX_FETCH_ALL_ACCOUNT_AMOUNTS);
  const parsedAccount = isSymbol(account) ? undefined : account;
  const { getMainCoinAmountData, getMainCoinsAvailableAmountDataStatus } =
    useBotFormAccountAmounts({
      botType,
      botFormType,
      account: parsedAccount,
      symbol: isSymbol(symbols) ? undefined : symbols?.[0],
      botsData,
    });
  const { currentData: groupData, ...getGroupDataStatus } =
    sharedLib.sharedApi.common.useGetGroupDataQuery(
      subGroup ? { sub_group_id: subGroup.id } : skipToken
    );
  const [initialized, setInitialized] = useState(false);
  const [initializedSubGroup, setInitializedSubGroup] = useState<SubGroup>();
  const symbolsMetadata = useSymbolsMetadata();
  const { getExchangeTag } = useExchangesMetadata();
  const {
    exchangeToSymbols,
    isSymbolActiveInAccount,
    getMainCoin,
    getExchangeToSymbolsStatus,
    getExchangeSymbols,
    getAccountActiveSymbols,
    getQuantityCoin,
    isSuccess,
  } = (botType ? botFormSymbolsMetadata : symbolsMetadata) || {};
  const selectedExchangeTag = isSymbol(account)
    ? undefined
    : getExchangeTag(account?.exchange);
  const isTradingViewBot =
    botType === BotType.TRADING_VIEW ||
    botType === BotType.TRADING_VIEW_SIGNALS;
  const shouldShowAlertPlaceholder = isTradingViewBot && useSymbolFromAlert;
  const isHedgingAccountValid = useCallback(
    (account?: Pick<ClientData, "exchange" | "is_hedge_mode">) =>
      !isHedgingBot ||
      (!!account &&
        MARGIN_EXCHANGES.has(account.exchange) &&
        account.is_hedge_mode === true),
    [isHedgingBot]
  );

  useEffect(() => {
    if (
      shouldInitialSymbolAndAccount &&
      isPublicBotApplied &&
      publicBotGeneralData
    ) {
      setInitialized(false);
      botFormSetAccount?.(undefined);
      setIsModifiedSymbols(false);
      setSwitchedExchange(undefined);
      setShouldInitialSymbolAndAccount?.(false);
    }
  }, [
    shouldInitialSymbolAndAccount,
    isPublicBotApplied,
    botUUID,
    publicBotGeneralData,
  ]);

  useCopyBotToWorkspace({
    getMainCoinAmountData,
    getMainCoinsAvailableAmountDataStatus,
  });

  const isExchangeValidLeverageType = useCallback(
    (exchange: string) => {
      if (!isBulkCreate || !exchange || isSymbol(account) || !account)
        return true;
      return getExchangeTag(exchange) === selectedExchangeTag;
    },
    [selectedExchangeTag, isBulkCreate, account]
  );

  const getExchangeErrorMessage = useCallback(
    (exchange: string) => {
      if (!isExchangeValidLeverageType(exchange)) {
        return t("symbol_and_account.exchange_selected", {
          selectedExchangeTag,
          lowerSelectedExchangeTag: selectedExchangeTag?.toLowerCase(),
        });
      }
    },
    [selectedExchangeTag, isExchangeValidLeverageType]
  );

  const getAccountErrorMessage = useCallback(
    (account: ClientData) => {
      return (
        getExchangeErrorMessage(account.exchange) ||
        (!isHedgingAccountValid(account)
          ? t("symbol_and_account.hedging_margin_account_required")
          : undefined)
      );
    },
    [getExchangeErrorMessage, isHedgingAccountValid]
  );

  const signalPairs = useMemo(() => {
    const pairs = new Set<string>([]);
    mapValues(
      groupData?.exchange_to_main_coin_to_last_month_signals_count,
      (value) => {
        Object.keys(value).forEach((mainCoin) => pairs.add(mainCoin));
      }
    );
    return [...pairs];
  }, [groupData]);

  const exchangeToNumSignals = useMemo(() => {
    return mapValues(
      groupData?.exchange_to_main_coin_to_last_month_signals_count,
      (value) => {
        return sum(Object.values(value));
      }
    );
  }, [groupData]);

  const maxNumSignals = useMemo(() => {
    return max(Object.values(exchangeToNumSignals));
  }, [exchangeToNumSignals]);

  const sortedAccounts = useMemo(() => {
    return writableActiveClientsData
      ?.slice()
      .map((account) => {
        return {
          ...account,
          isInvalid: !!getAccountErrorMessage(account),
        };
      })
      .sort((a, b) => sortByStringProperty(a, b, "client_name"));
  }, [writableActiveClientsData, getAccountErrorMessage]);

  const defaultSelectableAccounts = useMemo(() => {
    if (!isHedgingBot) return sortedAccounts;
    return sortedAccounts?.filter((account) => isHedgingAccountValid(account));
  }, [isHedgingBot, sortedAccounts, isHedgingAccountValid]);

  const defaultAccount = useMemo(() => {
    return defaultSelectableAccounts?.find(
      (account) => account.client_id === defaultAccountId
    );
  }, [defaultAccountId, defaultSelectableAccounts]);

  const defaultSymbols = useMemo(() => {
    let defaultSymbols = propsDefaultSymbols;
    if (!isTrade && isSignalsBot && isEdit) {
      if (!defaultSymbols) {
        if (defaultExchange) {
          defaultSymbols = getExchangeSymbols?.(defaultExchange);
        } else if (defaultAccount) {
          defaultSymbols = getAccountActiveSymbols?.(defaultAccount.client_id);
        }
      }
      if (defaultBlacklistSymbols) {
        const blacklistSet = new Set(defaultBlacklistSymbols);
        defaultSymbols = defaultSymbols?.filter(
          (symbol) => !blacklistSet.has(symbol)
        );
      }
    }
    return defaultSymbols;
  }, [
    propsDefaultSymbols,
    defaultBlacklistSymbols,
    getExchangeSymbols,
    isSuccess,
    defaultAccount,
  ]);

  const defaultSymbol = isSignalsBot ? undefined : defaultSymbols?.[0];
  const exchange = parsedAccount?.exchange;
  const symbolsIsMultipleValues = isSymbol(symbols);

  const exchangeSymbols = useMemo(() => {
    return isBulkCreate
      ? accountsOverlapData?.symbols?.allOptions || []
      : getAccountActiveSymbols?.(parsedAccount?.client_id) || [];
  }, [exchange, getAccountActiveSymbols, accountsOverlapData]);

  const validateSymbolInSelectedExchange = useCallback(
    (symbol: string) => {
      return !exchange || exchangeSymbols.includes(symbol);
    },
    [exchange, exchangeSymbols]
  );

  const findAccountForSymbol = (symbol: string) =>
    defaultSelectableAccounts?.find((iterAccount) =>
      isSymbolActiveInAccount?.(symbol, iterAccount.client_id)
    );

  const getBotMinMainCoinAmount = (exchange?: string, mainCoin?: string) => {
    if (publicBotGeneralData && exchange && mainCoin) {
      return publicBotGeneralData.public_data?.min_bot_amount?.[exchange]?.[
        mainCoin
      ]?.amount;
    }
    return 0;
  };

  const sortByLeverage = (
    a: ExchangeSymbolAmountData,
    b: ExchangeSymbolAmountData
  ) => {
    const aHasSameLeverageType =
      !!isMarginExchange === MARGIN_EXCHANGES.has(a.account.exchange);
    const bHasSameLeverageType =
      !!isMarginExchange === MARGIN_EXCHANGES.has(b.account.exchange);
    return Number(bHasSameLeverageType) - Number(aHasSameLeverageType);
  };

  const getSignalsBotSymbolAndExchangesRanking = () => {
    if (
      getMainCoinsAvailableAmountDataStatus?.isFetching ||
      !initializedData?.has(FormDataType.SUB_GROUP) ||
      (subGroup && !groupData)
    )
      return;
    const data: ExchangeSymbolAmountData[] = [];
    defaultSelectableAccounts?.forEach((account) => {
      const usdtAmountData = getMainCoinAmountData(account.client_id, "USDT");
      const allPairsSum = signalPairs.reduce((acc, mainCoin) => {
        const mainCoinAmountData = getMainCoinAmountData(
          account.client_id,
          mainCoin
        );
        const numPairSignals =
          groupData?.exchange_to_main_coin_to_last_month_signals_count?.[
            account.exchange
          ]?.[mainCoin] || 0;
        const pairUsdAmount = mainCoinAmountData?.usd_amount || 0;
        return acc + pairUsdAmount * numPairSignals;
      }, 0);
      data.push({
        account,
        symbol: "",
        mainCoin: "",
        mainCoinUSDAmount: allPairsSum,
        usdAmount: usdtAmountData?.usd_amount || 0,
        ratio: 0,
      });
    });
    const useDefaultUsdSort = data.every(
      (dataItem) => !dataItem.mainCoinUSDAmount
    );
    const accountIdsWithSymbolsOverlap = new Set(
      defaultSelectableAccounts
        ?.filter((account) => {
          const accountSymbols = getAccountActiveSymbols?.(account.client_id);
          return intersection(accountSymbols, defaultSymbols).length;
        })
        .map((account) => account.client_id)
    );

    let filteredData = data.filter((item) => {
      const numSignals = exchangeToNumSignals[item.account.exchange];
      return (
        !maxNumSignals || (!!numSignals && numSignals / maxNumSignals > 0.2)
      );
    });

    if (!filteredData.length) filteredData = data;
    return filteredData.sort((a, b) => {
      const aHasSymbolsOverlap = accountIdsWithSymbolsOverlap?.has(
        a.account.client_id
      );
      const bHasSymbolsOverlap = accountIdsWithSymbolsOverlap?.has(
        b.account.client_id
      );
      const symbolsOverlapSort =
        Number(bHasSymbolsOverlap) - Number(aHasSymbolsOverlap);
      const amountSort = sortByNumberProperty(
        a,
        b,
        useDefaultUsdSort ? "usdAmount" : "mainCoinUSDAmount",
        SortingOrder.DESCEND
      );
      return sortByLeverage(a, b) || symbolsOverlapSort || amountSort;
    });
  };

  const getSymbolAndExchangesRanking = (symbol?: string, exchange?: string) => {
    if (
      getMainCoinsAvailableAmountDataStatus?.isFetching ||
      getGroupDataStatus.isFetching ||
      !symbol ||
      !exchange
    )
      return;

    const data: ExchangeSymbolAmountData[] = [];
    const equivalentSymbols = uniq([
      symbol,
      ...getPossibleEquivalentSymbols(symbol),
    ]);
    defaultSelectableAccounts?.forEach((account) => {
      const { exchange, client_id } = account;
      equivalentSymbols.forEach((equivalentSymbol) => {
        const mainCoin = getMainCoin?.(equivalentSymbol, exchange);
        const mainCoinMinAmount = publicBotGeneralData?.public_data
          ? getBotMinMainCoinAmount(exchange, mainCoin)
          : 1;
        const mainCoinAmountData = getMainCoinAmountData(client_id, mainCoin);
        if (
          isSymbolActiveInAccount?.(equivalentSymbol, client_id) &&
          mainCoin &&
          (!publicBotGeneralData || mainCoinMinAmount)
        ) {
          data.push({
            account,
            symbol: equivalentSymbol,
            mainCoin,
            mainCoinUSDAmount: mainCoinAmountData?.usd_amount || 0,
            ratio:
              mainCoinMinAmount && shouldFetchAllAccountsAmounts
                ? (mainCoinAmountData?.amount || 0) / mainCoinMinAmount
                : isTrade
                ? (mainCoinAmountData?.usd_amount || 0) /
                  getMinUsdAmountPerTrade(account.exchange)
                : 0,
          });
        }
      });
    });
    const sortKey = publicBotGeneralData ? "ratio" : "mainCoinUSDAmount";
    const sortedData = data.sort((a, b) => {
      return sortByNumberProperty(a, b, sortKey, SortingOrder.DESCEND);
    });
    if (publicBotGeneralData) {
      sortedData.sort(sortByLeverage);
    }
    return sortedData;
  };

  const findAmountData = (
    amountsData: ExchangeSymbolAmountData[],
    symbol?: string,
    exchange?: string
  ) => {
    return amountsData.find(
      (amountData) =>
        (!exchange || amountData.account.exchange === exchange) &&
        (!symbol || amountData.symbol === symbol)
    );
  };

  const searchAmountsData = (
    amountsData: ExchangeSymbolAmountData[],
    symbol?: string,
    exchange?: string
  ) => {
    let amountData: ExchangeSymbolAmountData | undefined;
    if (symbol && exchange) {
      amountData = findAmountData(amountsData, symbol, exchange);
    }
    if (!amountData && symbol) {
      amountData = findAmountData(amountsData, symbol);
    }
    return amountData;
  };

  const initSignalsBotOverlappingSymbols = (account?: ClientData) => {
    const exchange = account?.exchange;
    const accountSymbols = getAccountActiveSymbols?.(account?.client_id);
    const originalBotExchange = defaultAccount?.exchange ?? defaultExchange;

    let commonSymbols = accountSymbols;
    if (defaultSymbols) {
      commonSymbols = intersection(accountSymbols, defaultSymbols);
    }

    let addedSymbols: string[] = [];
    if (originalBotExchange && originalBotExchange !== exchange) {
      const originalBotExchangeSymbols =
        getAccountActiveSymbols?.(defaultAccount?.client_id) ??
        getExchangeSymbols?.(originalBotExchange) ??
        [];
      addedSymbols = difference(accountSymbols, originalBotExchangeSymbols);
    }

    const symbols = union(commonSymbols, addedSymbols);
    setSymbols?.(symbols);
    if (isCopyPublic) {
      if (!!addedSymbols.length) {
        setIsModifiedSymbols(true);
      } else if (isModifiedSymbols) {
        setIsModifiedSymbols(false);
      }
    }
  };

  const setPublicCopyAccountAndSymbol = () => {
    const sortedAmountsData = isSignalsBot
      ? getSignalsBotSymbolAndExchangesRanking()
      : getSymbolAndExchangesRanking(defaultSymbol, defaultExchange);
    if (!sortedAmountsData) return;
    let amountData: ExchangeSymbolAmountData | undefined;
    if (isSignalsBot) {
      amountData = sortedAmountsData?.[0];
    } else {
      const validAmountsData = sortedAmountsData.filter(
        (amountData) => amountData.ratio > 1
      );
      const invalidAmountsData = sortedAmountsData.filter(
        (amountData) => amountData.ratio <= 1
      );
      amountData =
        searchAmountsData(validAmountsData, defaultSymbol, defaultExchange) ||
        validAmountsData[0];
      if (!amountData) {
        amountData = searchAmountsData(
          invalidAmountsData,
          defaultSymbol,
          defaultExchange
        );
      }
    }

    if (amountData) {
      const { symbol, account } = amountData;
      if (isSignalsBot) {
        initSignalsBotOverlappingSymbols(amountData.account);
      } else {
        if (defaultSymbol && symbol !== defaultSymbol) {
          setIsModifiedSymbols(true);
        } else if (defaultExchange && account.exchange !== defaultExchange) {
          setSwitchedExchange(account.exchange);
        }
        setSymbols?.([symbol]);
      }
      setAccount?.(account);
      setInitializedSubGroup(subGroup);
      setInitialized(true);
    } else if (defaultSymbol) {
      setInitializedSubGroup(subGroup);
      setInitialized(true);
      setSymbols?.([defaultSymbol]);
      const account =
        findAccountForSymbol(defaultSymbol) || defaultSelectableAccounts?.[0];
      if (account) {
        setAccount?.(account);
      }
    }
  };

  const getDefaultAccount = () => {
    if (defaultSelectableAccounts) {
      return sortItemsByExchangeOrder(defaultSelectableAccounts)?.[0];
    }
  };

  const setBotCreateAccountAndSymbol = () => {
    if (isChannelConfiguration) {
      return;
    }
    const amountData = isSignalsBot
      ? getSignalsBotSymbolAndExchangesRanking()?.[0]
      : getSymbolAndExchangesRanking("BTC/USDT", "Binance")?.[0];
    if (amountData) {
      if (!isSignalsBot) {
        setSymbols?.([amountData.symbol]);
      } else if (!isBacktesting) {
        const accountSymbols =
          getAccountActiveSymbols?.(amountData.account?.client_id) || [];
        setSymbols?.(accountSymbols);
      }
      if (botType !== BotType.TRADING_VIEW_SIGNALS) {
        setAccount?.(amountData.account);
      }
      setInitialized(true);
      setInitializedSubGroup(subGroup);
    }
  };

  const initializeAccountAndSymbol = () => {
    if (
      (botFormType === BotFormType.CREATE &&
        !defaultAccountId &&
        !isBacktesting &&
        !isPublicBotApplied) ||
      isBulkCreate ||
      (isTrade && !useFreeText)
    ) {
      setBotCreateAccountAndSymbol();
    } else if (
      botFormType === BotFormType.COPY_PUBLIC ||
      (isTrade && useFreeText && !defaultAccountId)
    ) {
      setPublicCopyAccountAndSymbol();
    } else if (defaultSelectableAccounts?.length) {
      let account: ClientData | undefined;
      if (defaultAccountId && !isChannelConfiguration) {
        account = defaultSelectableAccounts.find(
          (account) => account.client_id === defaultAccountId
        );
      }
      if (account) {
        setAccount?.(account);
        if (isSignalsBot) {
          initSignalsBotOverlappingSymbols(account);
        } else if (defaultSymbols) {
          setSymbols?.(defaultSymbols);
        }
      }
      setInitializedSubGroup(subGroup);
      setInitialized(true);
    }
  };

  const showNoAccountExistsForSymbol = useCallback(() => {
    const symbol =
      symbolsIsMultipleValues || !symbols?.length ? undefined : symbols[0];
    const accountExistsForSymbol = writableActiveClientsData?.some((account) =>
      isSymbolActiveInAccount?.(symbol, account.client_id)
    );
    return (
      !symbolsIsMultipleValues &&
      symbols?.length &&
      !!symbolsMetadata &&
      !accountExistsForSymbol
    );
  }, [
    symbolsIsMultipleValues,
    symbols,
    writableActiveClientsData,
    symbolsMetadata,
  ]);

  const shouldPreserveSelectedAccount = (): boolean =>
    isSignalsBot &&
    botFormType === BotFormType.CREATE &&
    !!parsedAccount &&
    initialized &&
    !isBacktesting;

  const getShouldInitialize = (): boolean => {
    const alreadySetValues =
      (!symbolsIsMultipleValues && account && !!symbols?.length) ||
      (!isTrade && initialized);
    const groupNeedsInitialization =
      initializedSubGroup !== subGroup && !shouldPreserveSelectedAccount();
    return (
      !isCopyToWorkspace &&
      !!exchangeToSymbols &&
      getClientsListStatus.isSuccess &&
      ((!!initialize && !alreadySetValues) || groupNeedsInitialization)
    );
  };

  useEffect(() => {
    if (!getShouldInitialize()) {
      return;
    }
    if (
      !shouldFetchAllAccountsAmounts &&
      !account &&
      !(isEdit || isBacktesting)
    ) {
      const account = getDefaultAccount();
      setAccount?.(account);
      if (isSignalsBot) {
        initSignalsBotOverlappingSymbols(account);
      }
    } else {
      initializeAccountAndSymbol();
      addInitializedData?.(FormDataType.ACCOUNT_AND_SYMBOL);
    }
  }, [
    initialize,
    exchangeToSymbols,
    sortedAccounts,
    defaultSelectableAccounts,
    symbols,
    account,
    botAmountData,
    getExchangeToSymbolsStatus?.isSuccess,
    getMainCoinsAvailableAmountDataStatus.isSuccess,
    getGroupDataStatus.isSuccess,
    exchangeToNumSignals,
    isTrade,
    useFreeText,
    subGroup,
    signalPairs,
    isPublicBotApplied,
    initialized,
  ]);

  const defaultMainCoin = getMainCoin?.(defaultSymbol, defaultExchange);
  const [symbolErrorMessage, symbolIsValid, symbolErrorMessageHelperData] =
    useMemo(() => {
      if (!symbolsIsMultipleValues && account) {
        if (!symbols?.length && !isTradingViewBot) {
          return [
            isSignalsBot
              ? t("symbol_and_account.select_at_least_one_symbol")
              : t("symbol_and_account.select_symbol"),
            false,
          ];
        } else if (isTrade && showNoAccountExistsForSymbol()) {
          return [
            t("symbol_and_account.symbol_not_available_in_connected_accounts"),
            false,
          ];
        } else if (
          !isSignalsBot &&
          symbols?.[0] &&
          !validateSymbolInSelectedExchange(symbols[0])
        ) {
          return [
            t("symbol_and_account.symbol_not_available_on_selected_exchange"),
            false,
          ];
        } else if (isModifiedSymbols) {
          if (isSignalsBot) {
            return [
              t("symbol_and_account.bot_symbols_different"),
              true,
              getHelperData("signalsBotSymbolModifiedWarning"),
            ];
          } else if (defaultSymbol && defaultMainCoin && !isTrade) {
            return [
              t("symbol_and_account.bot_symbol_changed", {
                defaultSymbol,
                defaultMainCoin,
              }),
              true,
            ];
          }
        }
      }
      return [undefined, true];
    }, [
      symbols,
      account,
      isModifiedSymbols,
      exchangeToSymbols,
      defaultSymbol,
      defaultExchange,
      defaultMainCoin,
      showNoAccountExistsForSymbol,
      isTrade,
      validateSymbolInSelectedExchange,
    ]);

  const [accountErrorMessage, accountIsValid] = useMemo(() => {
    if (isCopyToWorkspace && !account) {
      return [t("symbol_and_account.no_match_account_in_workspace"), false];
    }
    if (showNoAccountExistsForSymbol() && !isTrade)
      return [
        t("symbol_and_account.symbol_not_available_in_connected_accounts"),
        false,
      ];
    if (!account) return [t("symbol_and_account.select_account"), false];
    if (!isSymbol(account) && !isHedgingAccountValid(account)) {
      return [t("symbol_and_account.hedging_margin_account_required"), false];
    }
    if (switchedExchange && defaultExchange && defaultMainCoin && !isTrade) {
      return [
        t("symbol_and_account.exchange_changed", {
          item: isTrade
            ? t("symbol_and_account.lower_account")
            : t("symbol_and_account.bot_exchange"),
          defaultMainCoin,
          defaultExchange,
          tradeOrBot: isTrade
            ? t("symbol_and_account.trade")
            : t("symbol_and_account.bot"),
        }),
        true,
      ];
    }
    if (defaultExchange && exchange !== defaultExchange && !isTrade)
      return [
        t("symbol_and_account.different_exchange", {
          defaultExchange,
          suffix: isSignalsBot
            ? t("symbol_and_account.different_exchange_signals_bot_suffix")
            : ".",
        }),
        true,
      ];
    return [undefined, true];
  }, [
    symbols,
    account,
    switchedExchange,
    defaultExchange,
    defaultMainCoin,
    exchange,
    isTrade,
    showNoAccountExistsForSymbol,
    isHedgingAccountValid,
  ]);

  const onAccountChange = (accountId: number | number[]) => {
    const previousAccountIds = isSymbol(accounts)
      ? undefined
      : accounts?.map((account) => account.client_id);
    const accountIds = Array.isArray(accountId) ? accountId : [accountId];
    const addedAccountId = difference(
      accountIds,
      previousAccountIds || []
    )?.[0];
    const addedAccount = addedAccountId
      ? getAccountById(addedAccountId)
      : undefined;
    const accountErrorMessage = addedAccount
      ? getAccountErrorMessage(addedAccount)
      : undefined;
    if (accountErrorMessage) {
      openMessage(accountErrorMessage, "warning");
      return;
    }
    const updatedAccounts = accountIds
      .map((accountId) => getAccountById(accountId))
      .filter((account) => !!account) as ClientData[];
    setSwitchedExchange(undefined);
    if (setAccounts) setAccounts(updatedAccounts);
    else setAccount?.(updatedAccounts[0]);
    if (isSignalsBot) {
      initSignalsBotOverlappingSymbols(updatedAccounts[0]);
    }
    onAccountChanged?.();
  };

  const onSymbolChange = (symbol?: string) => {
    if (symbol) {
      setSymbols?.([symbol]);
      setUseSymbolFromAlert?.(false);
      setIsModifiedSymbols(false);
      onSymbolChanged?.();
    }
  };

  const handleChange = (func: () => void) => {
    if (onChangeWithWarning) {
      onChangeWithWarning(func).catch(() => {});
    } else {
      func();
    }
  };

  const getAccountValue = () => {
    if (isSymbol(accounts) || isSymbol(account)) return undefined;
    else if (isBulkCreate) {
      return accounts?.map((account) => account.client_id.toString());
    } else return account?.client_id.toString();
  };

  const getExchanges = () => {
    let exchanges: string[] | undefined = undefined;
    if (isSymbol(account) || isSymbol(accounts)) {
      exchanges = (bulkActionBotsData as BulkEditBotData[])?.map(
        (bot) => bot.exchange
      );
    } else if (accounts && accounts?.length > 1) {
      exchanges = accounts?.map((account) => account.exchange);
    }
    return uniq(exchanges);
  };

  const showAccountEmptyState = !writableActiveClientsData?.length;
  const accountSelectorData = {
    value: getAccountValue(),
    onChange: (accountId: string | string[]) => {
      const accountIds = Array.isArray(accountId) ? accountId : [accountId];
      handleChange(() =>
        onAccountChange(accountIds.map((accountId) => parseInt(accountId)))
      );
    },
    options: sortedAccounts,
    title: isSymbol(account)
      ? t("symbol_and_account.accounts")
      : t("symbol_and_account.account"),
    exchanges: getExchanges(),
    isMultiSelect: isBulkCreate,
    showEmptyState: showAccountEmptyState,
  };

  const getCoins = () => {
    return uniq(
      (bulkActionBotsData as BulkEditBotData[])
        ?.map((bot) => getQuantityCoin?.(bot.symbols?.[0], bot.exchange, true))
        .filter((coin) => !!coin)
    ) as string[];
  };

  const symbolSelectorData = {
    value: symbolsIsMultipleValues
      ? undefined
      : shouldShowAlertPlaceholder
      ? TV_ALERT_SYMBOL
      : symbols?.[0],
    onChange: (symbol: string) => handleChange(() => onSymbolChange(symbol)),
    options: exchangeSymbols,
    disabled:
      shouldShowAlertPlaceholder ||
      (!symbolsIsMultipleValues &&
        !!symbols?.length &&
        !!exchange &&
        !validateSymbolInSelectedExchange(symbols[0])),
    title:
      isSymbol(symbols) || isSignalsBot
        ? t("symbol_and_account.symbols")
        : t("symbol_and_account.symbol"),
    coins: getCoins(),
  };

  return {
    accountErrorMessage,
    accountIsValid,
    symbolErrorMessage,
    symbolErrorMessageHelperData,
    symbolIsValid,
    exchangeSymbols,
    accountSelectorData,
    symbolSelectorData,
    getExchangeErrorMessage,
    getAccountErrorMessage,
  };
};

export default useSharedSymbolAndAccountSelectors;
